import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Link2, Copy, Trash2, Share2 } from "lucide-react";

export default function WatchlistShareDialog({ open, onOpenChange }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/watchlist/shares");
      setShare(r.data?.shares?.[0] || null);
    } catch (e) {
      logger.warn("watchlist share list", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const shareUrl = share ? `${window.location.origin}/w/${share.token}` : "";

  const create = async () => {
    setLoading(true);
    try {
      const r = await api.post("/watchlist/share");
      setShare({ token: r.data.token, created_at: r.data.created_at });
      toast.success("Enlace creado");
    } catch {
      toast.error("No se pudo crear");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const revoke = async () => {
    if (!share || !window.confirm("¿Revocar este enlace? El acceso se cortará al instante.")) return;
    try {
      await api.delete(`/watchlist/share/${share.token}`);
      toast.success("Enlace revocado");
      setShare(null);
    } catch {
      toast.error("No se pudo revocar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#1C7C54]" />
            Compartir watchlist
          </DialogTitle>
          <p className="text-xs text-[#8A9992]">
            Enlace público de solo lectura. Se muestran precios, variaciones y objetivos.
            No se comparten cantidades, saldos ni identidad más allá de tu nombre.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-sm text-[#5F7368]">Cargando…</div>
        ) : !share ? (
          <div className="flex flex-col gap-3 py-2">
            <div className="text-sm text-[#0A110E]">
              Aún no has generado ningún enlace.
            </div>
            <Button
              onClick={create}
              data-testid="watchlist-share-create-btn"
              className="bg-[#0F291E] hover:bg-[#1C7C54] self-start"
            >
              <Link2 className="w-4 h-4 mr-2" /> Generar enlace
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-2">
            <Input
              readOnly
              value={shareUrl}
              data-testid="watchlist-share-url"
              className="text-xs font-mono"
              onFocus={(e) => e.target.select()}
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={copy}
                data-testid="watchlist-share-copy-btn"
                className="bg-[#0F291E] hover:bg-[#1C7C54]"
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar
              </Button>
              <Button
                variant="ghost"
                onClick={revoke}
                data-testid="watchlist-share-revoke-btn"
                className="text-[#D95D39] hover:text-[#D95D39] hover:bg-[#D95D39]/10 ml-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Revocar
              </Button>
            </div>
            {share.created_at && (
              <div className="text-[11px] text-[#8A9992]">
                Creado el {share.created_at.slice(0, 10)}. Cualquiera con el enlace verá tu watchlist hasta que lo revoques.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
